/**
 * Recovery for stale-chunk errors.
 *
 * After a new deploy, tabs that had the app open before the deploy still run the OLD
 * `index.js`, which references OLD content-hashed lazy chunks. Navigating to a
 * code-split route then fails to fetch the (now-deleted) chunk, throwing a
 * "stale chunk" error. The fix is a fresh full reload, which picks up the new
 * `index.html` / `index.js` and therefore the new chunk URLs.
 *
 * Two layers call into this module:
 *  1. The `vite:preloadError` listener registered below (fires when a dynamic
 *     `import()` preload fails — the common case).
 *  2. The top-level ErrorBoundary (`src/AppErrorBoundary.tsx`, wired in `main.tsx`),
 *     which reuses `isChunkLoadError` and `guardedReload` as a backstop for chunk
 *     errors that surface as a thrown error instead of a `vite:preloadError` event.
 *
 * `guardedReload` is cooldown-guarded via `sessionStorage` so a persistent failure
 * (e.g. the server is actually down) reloads once and then stops, instead of
 * looping forever.
 *
 * Reporting follows recovery: a chunk error we reload out of is a deploy doing what deploys
 * do, and the user never sees it, so `main.tsx` drops it in `beforeSend` when
 * `chunkRecoveryStarted()` is true. What still reaches Sentry is the case worth knowing about —
 * the cooldown refused a second reload, so the chunk is genuinely unreachable and the user is
 * looking at the fallback screen.
 */

/**
 * Set the moment a reload is triggered and never cleared: the reload replaces this page, so the
 * flag only lives long enough for the in-flight error report to see it.
 */
let recoveryStarted = false;

/** Whether a chunk-load reload has been triggered during this page view. */
export function chunkRecoveryStarted(): boolean {
  return recoveryStarted;
}

const RELOAD_GUARD_KEY = 'chunk-reload:lastAt';
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
];

/** True if `err` looks like a stale/failed dynamic-import (lazy chunk) error. */
export function isChunkLoadError(err: unknown): boolean {
  let message = '';
  if (err instanceof Error) {
    message = err.message;
  } else if (typeof err === 'string') {
    message = err;
  }
  if (!message) return false;
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Reloads the page once, then suppresses further reloads for `RELOAD_COOLDOWN_MS`.
 * Returns whether a reload was actually triggered (false means the cooldown is
 * still active, i.e. we already tried this recently and it didn't help).
 *
 * `sessionStorage` access is wrapped in try/catch: in private browsing or a
 * sandboxed iframe it can throw on get/set instead of just being unavailable,
 * and this runs inside an event handler where an uncaught throw would be
 * silently lost. If storage is unusable we can't remember "did we already
 * reload", so we fail safe by treating it as "never reloaded" — this allows at
 * most one reload per storage failure (the reload itself navigates away,
 * which can't loop within the same call), rather than skipping the reload
 * and leaving the user stuck on a dead chunk.
 */
export function guardedReload(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
  } catch {
    // Storage unreadable — proceed as if no prior reload was recorded.
  }
  if (Date.now() - last < RELOAD_COOLDOWN_MS) {
    // Already reloaded very recently — reloading again would loop forever.
    return false;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Storage unwritable — nothing to persist, but still proceed with the
    // single reload below (see rationale above).
  }
  recoveryStarted = true;
  window.location.reload();
  return true;
}

/** Registers the `vite:preloadError` listener. Call once, before the app renders. */
export function registerChunkReloadListener(): void {
  window.addEventListener('vite:preloadError', () => {
    // Deliberately NOT calling event.preventDefault() here. Vite's internal
    // handlePreloadError only rethrows the original chunk-load error when the
    // event is not default-prevented (`if (!e.defaultPrevented) throw err;`).
    // If we prevented the default, the rejected import() would resolve to
    // `undefined` instead of rejecting, and App.tsx's
    // `lazy(() => import(...).then(m => ({ default: m.X })))` would then throw
    // a NEW, unrelated `TypeError: Cannot read properties of undefined
    // (reading 'X')` — a message that doesn't match CHUNK_LOAD_ERROR_PATTERNS,
    // so AppErrorBoundary would render the generic fallback (no Reload
    // button) and report a full-severity, misleading error to Sentry instead
    // of downgrading/tagging it as a benign chunk-load error.
    //
    // By letting the event propagate un-prevented, the rejected import()
    // stays rejected — a rejected promise skips the `.then(onFulfilled)`
    // callback entirely — so the ORIGINAL chunk error propagates through
    // React.lazy/Suspense to AppErrorBoundary, which classifies it correctly
    // via `isChunkLoadError`.
    //
    // We still proactively call guardedReload() here so the common case
    // (stale tab after a deploy) recovers immediately without ever showing
    // the fallback UI. If the cooldown guard suppresses this reload (e.g. we
    // already tried within the last 10s), the propagated real error is still
    // correctly handled by the boundary as a backstop.
    guardedReload();
  });
}
